import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Switch } from '@mui/material';
import { Settings, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { 
  GANTT_SETTINGS_SECTIONS, 
  GANTT_CONFIG_OPTIONS, 
  GANTT_GROUP_BY_OPTIONS
} from '../../../shared/appConstants';
import './GanttSettings.scss';

const GanttSettings = ({ 
  configOptions = {}, 
  onConfigChange,
  groupByOption,
  onGroupByChange
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    [GANTT_SETTINGS_SECTIONS.CONFIGURATIONS]: true,
    [GANTT_SETTINGS_SECTIONS.GROUP_BY]: false
  });
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleConfigToggle = (option) => {
    onConfigChange?.({
      ...configOptions,
      [option]: !configOptions[option]
    });
  };

  const handleGroupByToggle = (option) => {
    onGroupByChange?.(groupByOption === option ? null : option);
  };

  const renderSection = (sectionKey, options, selectedValue, onToggle, isConfig = false) => {
    const isExpanded = expandedSections[sectionKey];
    
    return (
      <Box className="gantt-settings-section">
        <Box 
          className="gantt-settings-section-header"
          onClick={() => toggleSection(sectionKey)}
        >
          <span className="gantt-settings-section-title">
            {t(`gantt.settings.${sectionKey}`)}
          </span>
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </Box>
        
        {isExpanded && (
          <Box className="gantt-settings-section-content">
            {Object.entries(options).map(([key, value]) => (
              <Box key={key} className="gantt-settings-option">
                <span className="gantt-settings-option-label">
                  {t(`gantt.settings.${value}`)}
                </span>
                <Switch
                  checked={isConfig ? !!configOptions[value] : selectedValue === value}
                  onChange={() => onToggle(value)}
                  size="small"
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box className="gantt-settings" ref={dropdownRef}>
      <IconButton 
        className="gantt-settings-icon"
        onClick={() => setIsOpen(!isOpen)}
        size="small"
      >
        <Settings />
      </IconButton>

      {isOpen && (
        <Box className="gantt-settings-dropdown custom-scrollbar">
          {renderSection(
            GANTT_SETTINGS_SECTIONS.CONFIGURATIONS,
            GANTT_CONFIG_OPTIONS,
            null,
            handleConfigToggle,
            true
          )}
          
          {renderSection(
            GANTT_SETTINGS_SECTIONS.GROUP_BY,
            GANTT_GROUP_BY_OPTIONS,
            groupByOption,
            handleGroupByToggle
          )}
        </Box>
      )}
    </Box>
  );
};

export default GanttSettings;
